/** @format */

import React, {useContext, useEffect} from 'react';
import {Checkbox, IconButton} from '@sttark/design-system';
import {QuoteContext} from 'frontier-quoter';
import {isNarrowLabel, getMaxRollDiameterForWidth} from 'frontier/utilities/roll_min_max_validator';
import {calcQtyFromDiameter, calcDiameterFromQty} from 'frontier-utilities';
import {ROLL_SIZE_TYPE_DIAMETER, ROLL_SIZE_TYPE_QTY} from 'frontier-constants';
import RollConstrainedInput from './roll_constrained_input';

/**
 * RollOptionsSection - A comprehensive component for managing roll quantity and diameter options
 * with clear mutual exclusivity and better user experience.
 */
export default function RollOptionsSection({errors}) {
    const ctx = useContext(QuoteContext);
    const order = ctx.order;

    const maxRollsError = errors.find(e => e.category === 'max_rolls');
    const maxQtyError = errors.find(e => e.category === 'max_qty');
    const maxDiameterError = errors.find(e => e.category === 'max_diameter');
    const designQtyError = errors.find(e => e?.subcategory === 'design_qty');

    // Check if special roll options are available
    const canSpecialRollQty = !errors.some(
        e => e.category === 'total_qty' || e.category === 'size'
    );

    // Account-level settings
    let accountRollOptionsEnabled = false;
    let accountRollDiameterMax = '';
    let accountRollQtyMax = '';
    let accountIsFewestPossibleRolls = false;

    if (
        ctx.cart?.user?.company_entity?.roll_max ||
        ctx.cart?.user?.company_entity?.is_fewest_possible_rolls
    ) {
        accountRollOptionsEnabled = true;
        accountRollDiameterMax =
            ctx.cart.user.company_entity.roll_type_id === ROLL_SIZE_TYPE_DIAMETER
                ? ctx.cart.user.company_entity.roll_max
                : '';
        accountRollQtyMax =
            ctx.cart.user.company_entity.roll_type_id === ROLL_SIZE_TYPE_QTY
                ? ctx.cart.user.company_entity.roll_max
                : '';
        accountIsFewestPossibleRolls = ctx.cart.user.company_entity.is_fewest_possible_rolls;

        console.log('Account roll settings detected:', {
            roll_type_id: ctx.cart.user.company_entity.roll_type_id,
            roll_max: ctx.cart.user.company_entity.roll_max,
            accountRollDiameterMax,
            accountRollQtyMax,
            accountIsFewestPossibleRolls
        });
    }

    const applyAccountDefaults = () => {
        const willApply =
            accountRollOptionsEnabled &&
            !designQtyError &&
            order.design_count &&
            order.design_qty &&
            order.total_qty;

        console.log('applyAccountDefaults called with:', {
            accountRollOptionsEnabled,
            designQtyError: !!designQtyError,
            'order.is_account_roll_defaults_applied': order.is_account_roll_defaults_applied,
            'order.roll_qty_max': order.roll_qty_max,
            'order.roll_diameter_max': order.roll_diameter_max,
            'order.is_special_roll_qty': order.is_special_roll_qty,
            'order.design_count': order.design_count,
            'order.design_qty': order.design_qty,
            'order.total_qty': order.total_qty,
            will_apply: willApply
        });

        if (!willApply) {
            console.log('❌ Account defaults NOT applied. Missing conditions:', {
                has_account_settings: accountRollOptionsEnabled,
                no_design_qty_error: !designQtyError,
                has_design_count: !!order.design_count,
                has_design_qty: !!order.design_qty,
                has_total_qty: !!order.total_qty
            });
            return;
        }

        // Only apply account defaults if order has design_count, design_qty, and total_qty
        if (
            willApply &&
            (!order.is_account_roll_defaults_applied ||
                (!order.roll_qty_max && !order.roll_diameter_max) ||
                !order.is_special_roll_qty)
        ) {
            // Enforce width-based constraints on company settings
            const maxAllowedDiameter = getMaxRollDiameterForWidth(
                order.width,
                order.product_type_id
            );
            let constrainedDiameterMax = accountRollDiameterMax;
            let constrainedQtyMax = accountRollQtyMax;

            // Constrain diameter if it exceeds width-based limit
            if (accountRollDiameterMax && accountRollDiameterMax > maxAllowedDiameter) {
                constrainedDiameterMax = maxAllowedDiameter;
            }

            // Constrain quantity if it would result in diameter exceeding width-based limit
            if (accountRollQtyMax && order.length && order.substrate_id) {
                const resultingDiameter = calcDiameterFromQty({
                    qty: accountRollQtyMax,
                    length: order.length,
                    substrateId: order.substrate_id,
                    isHighOpacityWhite: order.is_high_opacity_white
                });

                if (resultingDiameter > maxAllowedDiameter) {
                    constrainedQtyMax = calcQtyFromDiameter({
                        diameter: maxAllowedDiameter,
                        length: order.length,
                        substrateId: order.substrate_id,
                        isHighOpacityWhite: order.is_high_opacity_white
                    });
                }
            }

            console.log('✅ Applying account defaults:', {
                accountRollDiameterMax,
                accountRollQtyMax,
                constrainedDiameterMax,
                constrainedQtyMax,
                accountIsFewestPossibleRolls
            });

            ctx.handleUpdate({
                order: {
                    is_special_roll_qty: {$set: true},
                    is_account_roll_defaults_applied: {$set: true},
                    is_fewest_possible_rolls: {$set: accountIsFewestPossibleRolls},
                    roll_max: {$set: constrainedQtyMax || constrainedDiameterMax},
                    roll_diameter_max: {$set: constrainedDiameterMax || ''},
                    roll_qty_max: {$set: constrainedQtyMax || ''}
                }
            });

            console.log('✅ Account defaults applied successfully');
        }
        if (designQtyError && order.is_special_roll_qty) {
            ctx.handleUpdate({
                order: {
                    is_special_roll_qty: {$set: false},
                    is_account_roll_defaults_applied: {$set: false}
                }
            });
        }
    };

    const handleToggleSpecialRollQty = () => {
        const isEnabled = canSpecialRollQty ? order.is_special_roll_qty || false : false;
        ctx.handleUpdate({
            order: {
                is_special_roll_qty: {$set: !isEnabled},
                // Clear both values when toggling off
                roll_qty_max: {$set: !isEnabled ? '' : order.roll_qty_max},
                roll_diameter_max: {$set: !isEnabled ? '' : order.roll_diameter_max},
                roll_max: {$set: !isEnabled ? '' : order.roll_max}
            }
        });
    };

    const handleToggleFewestRolls = () => {
        const isEnabled = order.is_fewest_possible_rolls || false;
        ctx.handleUpdate({
            order: {
                is_fewest_possible_rolls: {$set: !isEnabled},
                is_account_roll_defaults_applied: {$set: true}
            }
        });
    };

    const handleQtyChange = value => {
        // Clear diameter when setting quantity
        ctx.handleUpdate({
            order: {
                roll_qty_max: {$set: value === '' ? '' : value},
                roll_diameter_max: {$set: ''},
                roll_max: {$set: value === '' ? '' : value},
                is_special_roll_qty: {$set: true},
                is_account_roll_defaults_applied: {$set: true}
            }
        });
    };

    const handleDiameterChange = value => {
        // Clear quantity when setting diameter
        ctx.handleUpdate({
            order: {
                roll_diameter_max: {$set: value === '' ? '' : value},
                roll_qty_max: {$set: ''},
                roll_max: {$set: value === '' ? '' : value},
                is_special_roll_qty: {$set: true},
                is_account_roll_defaults_applied: {$set: true}
            }
        });
    };

    // Determine current values - prioritize account settings if order values are empty
    const currentQtyValue =
        (!order.is_account_roll_defaults_applied && accountRollQtyMax) ||
        (!order.roll_qty_max && accountRollQtyMax)
            ? accountRollQtyMax
            : order.roll_qty_max;

    const currentDiameterValue =
        (!order.is_account_roll_defaults_applied && accountRollDiameterMax) ||
        (!order.roll_diameter_max && accountRollDiameterMax)
            ? accountRollDiameterMax
            : order.roll_diameter_max;

    // Simplified logic: use order value if it exists, otherwise use account value
    // Convert to boolean to handle string/number values from database
    const currentFewestRollsValue =
        order.is_fewest_possible_rolls !== undefined && order.is_fewest_possible_rolls !== null
            ? Boolean(order.is_fewest_possible_rolls)
            : Boolean(accountIsFewestPossibleRolls);

    console.log('Current input values:', {
        'order.is_account_roll_defaults_applied': order.is_account_roll_defaults_applied,
        accountRollQtyMax,
        'order.roll_qty_max': order.roll_qty_max,
        currentQtyValue,
        accountRollDiameterMax,
        'order.roll_diameter_max': order.roll_diameter_max,
        currentDiameterValue,
        accountIsFewestPossibleRolls,
        'typeof accountIsFewestPossibleRolls': typeof accountIsFewestPossibleRolls,
        'order.is_fewest_possible_rolls': order.is_fewest_possible_rolls,
        'typeof order.is_fewest_possible_rolls': typeof order.is_fewest_possible_rolls,
        currentFewestRollsValue,
        'typeof currentFewestRollsValue': typeof currentFewestRollsValue
    });

    // Calculate quantity limits based on label dimensions and diameter constraints
    const calculateQuantityLimits = () => {
        if (!order.length || !order.substrate_id || !order.width) {
            console.log('Missing required fields for quantity calculation:', {
                length: order.length,
                substrate_id: order.substrate_id,
                width: order.width
            });
            return {minQty: 100, maxQty: 10000}; // Fallback values
        }

        const maxDiameter = getMaxRollDiameterForWidth(order.width, order.product_type_id);
        const minDiameter = 4; // 4 inch minimum

        console.log('Calculating quantity limits with:', {
            maxDiameter,
            minDiameter,
            length: order.length,
            substrateId: order.substrate_id,
            width: order.width
        });

        try {
            const minQty = calcQtyFromDiameter({
                diameter: minDiameter,
                length: order.length,
                substrateId: order.substrate_id,
                isHighOpacityWhite: order.is_high_opacity_white
            });

            const maxQty = calcQtyFromDiameter({
                diameter: maxDiameter,
                length: order.length,
                substrateId: order.substrate_id,
                isHighOpacityWhite: order.is_high_opacity_white
            });

            console.log('Raw quantity calculations:', {minQty, maxQty});

            // Validate the results
            if (isNaN(minQty) || isNaN(maxQty)) {
                console.warn('NaN detected in quantity calculations:', {minQty, maxQty});
                return {minQty: 100, maxQty: 10000}; // Fallback values
            }

            const finalMinQty = Math.max(100, Math.round(minQty));
            const finalMaxQty = Math.round(maxQty);

            console.log('Final quantity limits:', {finalMinQty, finalMaxQty});

            return {
                minQty: finalMinQty,
                maxQty: finalMaxQty
            };
        } catch (error) {
            console.warn('Error calculating quantity limits:', error);
            return {minQty: 100, maxQty: 10000}; // Fallback values
        }
    };

    const {minQty, maxQty} = calculateQuantityLimits();

    // Consolidated useEffect for all logic - MUST be called before any early returns
    useEffect(() => {
        // Apply account defaults
        applyAccountDefaults();

        // Auto-adjust roll diameter when width changes and exceeds limits
        if (order.roll_diameter_max && order.width) {
            const currentDiameter = parseFloat(order.roll_diameter_max);
            const maxAllowedDiameter = getMaxRollDiameterForWidth(
                order.width,
                order.product_type_id
            );

            console.log('Auto-adjustment check:', {
                currentDiameter,
                maxAllowedDiameter,
                width: order.width,
                isNaN: isNaN(currentDiameter)
            });

            // If current diameter exceeds the new width-based limit, adjust it
            if (!isNaN(currentDiameter) && currentDiameter > maxAllowedDiameter) {
                console.log(
                    `Auto-adjusting roll diameter from ${currentDiameter}" to ${maxAllowedDiameter}" due to width change`
                );

                ctx.handleUpdate({
                    order: {
                        roll_diameter_max: {$set: maxAllowedDiameter},
                        roll_max: {$set: maxAllowedDiameter}
                    }
                });
            }
        }
    }, [
        order.is_special_roll_qty,
        order.width,
        order.product_type_id,
        order.roll_diameter_max,
        order.design_count,
        order.design_qty,
        order.total_qty
    ]);

    // Don't render if not enabled or has design qty errors
    if (!order.is_special_roll_qty || designQtyError) {
        return null;
    }

    return (
        <>
            <h6>Label Roll Options</h6>

            {accountRollOptionsEnabled && (
                <div className="alert alert-info mb-3">
                    <IconButton
                        secondary
                        large
                        className="color-accent-tertiary"
                        color="#e5b200"
                        icon="warning"
                    />{' '}
                    These values are pre-populated based on your chosen Production Presets. To edit
                    them globally, please visit your{' '}
                    <a href="/account/users/account-details" target="_blank">
                        Accounts
                    </a>{' '}
                    page.
                </div>
            )}

            <div className="row">
                <div className="col-12 mb-3">
                    <Checkbox
                        checked={currentFewestRollsValue}
                        onChange={handleToggleFewestRolls}
                        error={maxRollsError?.message}>
                        Produce fewest possible rolls
                    </Checkbox>
                </div>

                <div className="col-12 mb-3">
                    {isNarrowLabel(order.width, order.product_type_id) && (
                        <div className="alert alert-warning mb-3">
                            <IconButton
                                secondary
                                small
                                className="color-accent-tertiary"
                                color="#e5b200"
                                icon="warning"
                            />{' '}
                            Labels under 7/8" wide are limited to 8" diameter rolls for optimal
                            production quality.
                        </div>
                    )}

                    <div className="row">
                        <div className="col-md-6">
                            <RollConstrainedInput
                                label="Maximum labels per roll (+0/-10%)"
                                value={currentQtyValue}
                                onChange={() => {}} // No immediate updates needed
                                onCommit={handleQtyChange}
                                disabled={currentDiameterValue && currentDiameterValue !== ''}
                                error={maxQtyError?.message}
                                width={order.width}
                                productTypeId={order.product_type_id}
                                minValue={minQty}
                                maxValue={maxQty}
                                inputType="quantity"
                                className="mb-1"
                            />
                            {currentDiameterValue && currentDiameterValue !== '' && (
                                <small className="text-muted d-block mb-3">
                                    Disabled because diameter is set. Clear diameter to use
                                    quantity.
                                </small>
                            )}
                        </div>

                        <div className="col-md-6">
                            <RollConstrainedInput
                                label="Maximum Roll Diameter (+0/-10%)"
                                suffix='"'
                                value={currentDiameterValue}
                                onChange={() => {}} // No immediate updates needed
                                onCommit={handleDiameterChange}
                                disabled={currentQtyValue && currentQtyValue !== ''}
                                error={maxDiameterError?.message}
                                width={order.width}
                                productTypeId={order.product_type_id}
                                minValue={4}
                                inputType="diameter"
                                className="mb-1"
                            />
                            {currentQtyValue && currentQtyValue !== '' && (
                                <small className="text-muted d-block mb-3">
                                    Disabled because quantity is set. Clear quantity to use
                                    diameter.
                                </small>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
